import { DateTime } from 'luxon'
import { useMemo } from 'react'
import { createOpportunityListColumns } from '../-components/columns'
import type { OpportunityListItem } from './useOpportunities'

/**
 * オポチュニティタグをExcel出力用文字列に変換
 */
function formatOpportunityTags(tags: OpportunityListItem['tags']): string | null {
  if (!tags || tags.length === 0) return null
  return tags.map((tag) => tag.name).join(', ')
}

/**
 * Unixタイムスタンプ（秒）を日本語形式の日時文字列に変換
 */
function formatTimestampToDateTimeString(timestamp: number | null | undefined): string | null {
  if (!timestamp) return null
  const dt = DateTime.fromSeconds(Number(timestamp))
  return dt.isValid ? dt.toFormat('yyyy/MM/dd HH:mm:ss') : null
}

interface UseOpportunityColumnsOptions {
  /** 現在選択されているタブ */
  selectedTab: string
  /** アクションハンドラー */
  handlers: {
    onCommentClick: (opportunity: OpportunityListItem) => void
    onTagEditClick: (opportunity: OpportunityListItem) => void
    onCopyClick: (opportunity: OpportunityListItem) => void
    onStatusChangeClick: (opportunity: OpportunityListItem, statusId: number) => void
    onTagClick: (tagName: string) => void
  }
  /** ステータス一覧 */
  opportunityStatuses: Array<{
    id: number
    name: string
    color: string
    is_system_managed: boolean
    is_completed: boolean
  }>
  /** 前回のチェック時刻（Unixタイムスタンプ秒） */
  lastCheckAt: number
  /** 現在のユーザーのメンバーID */
  currentMemberId: number | null
}

/**
 * テーブルカラムとExcelカラムを生成するカスタムフック
 *
 * テーブル表示用のカラム定義とExcelエクスポート用のカラム定義を
 * useMemo で最適化して提供します。
 */
export function useOpportunityColumns(options: UseOpportunityColumnsOptions) {
  const { selectedTab, handlers, opportunityStatuses, lastCheckAt, currentMemberId } = options

  // カラム定義
  const columns = useMemo(
    () =>
      createOpportunityListColumns({
        selectedTab,
        handlers,
        opportunityStatuses,
        lastCheckAt,
        currentMemberId,
      }),
    [selectedTab, handlers, opportunityStatuses, lastCheckAt, currentMemberId],
  )

  // Excel 用のカラム定義（アクション列を除外）
  const excelColumns = useMemo(
    () =>
      columns
        .filter((col) => col.key !== 'action')
        .flatMap((col) => {
          // 日時カラムは専用のフォーマッターを使用
          if (col.key === 'updated_at' || col.key === 'created_at') {
            return [
              {
                label: col.label,
                accessor: (row: OpportunityListItem) =>
                  formatTimestampToDateTimeString(row[col.key as 'updated_at' | 'created_at']),
              },
            ]
          }
          // インセンティブカラムは「対象外」または空欄で出力
          if (col.key === 'disabled_at') {
            return [
              {
                label: col.label,
                accessor: (row: OpportunityListItem) => (row.disabled_at ? '対象外' : null),
              },
            ]
          }
          const basicColumn = {
            label: col.label,
            accessor: col.accessor || (() => null),
          }
          // 従業員番号も出力
          if (col.key === 'created_by_name') {
            return [
              basicColumn,
              {
                label: '起票者従業員番号',
                accessor: (row: OpportunityListItem) => row.created_by_employee_number,
              },
            ]
          }
          // オポチュニティタグも出力
          if (col.key === 'title') {
            return [
              basicColumn,
              {
                label: 'オポチュニティタグ',
                accessor: (row: OpportunityListItem) => formatOpportunityTags(row.tags),
              },
            ]
          }
          return [basicColumn]
        }),
    [columns],
  )

  return {
    columns,
    excelColumns,
  }
}
